/**
 * The org switcher.
 *
 * A `<select>` rather than a custom dropdown, deliberately: it is one of four
 * things in a sidebar that is otherwise placeholder, and a hand-rolled menu is
 * keyboard and focus work that item AE will redo anyway.
 *
 * Four states, all distinguishable, for the same reason `AgentTarget` has six:
 * loading, no session, an error, and a list — and the error names the failure
 * rather than rendering an empty switcher, which is what "your org disappeared"
 * looks like to a user whose token expired.
 */

import { useEffect, useRef, useState } from "react";
import { API_BASE_URL, isConfigured } from "../lib/config";
import {
  fetchOrganizations,
  OrganizationsError,
  type Organization,
} from "../lib/organizations";

export type OrgState =
  | { kind: "loading" }
  | { kind: "signed-out" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; organizations: Organization[] };

/**
 * The organizations for the signed-in user.
 *
 * Keyed on `userId`, **not** the access token, deliberately: the token rotates
 * often (the single-session guard refreshes it on focus, tab visibility and
 * every realtime reconnect; supabase auto-refreshes near expiry), but the orgs a
 * user belongs to do not change when it does. Keying on the token refetched
 * `/organizations` on every rotation — many redundant calls. The current token
 * is read from a ref at fetch time, so the request still carries a fresh one
 * without the rotation itself being a dependency.
 */
export function useOrganizations(userId: string | null, token: string | null): OrgState {
  const [state, setState] = useState<OrgState>({ kind: "loading" });

  // Latest token, updated every render but not a dependency — so a rotation does
  // not retrigger the fetch, yet the fetch below always uses the current value.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!userId) {
      setState({ kind: "signed-out" });
      return;
    }

    // An unconfigured build has no API to call, and `API_BASE_URL()`
    // throws by design when the env var is missing — calling it here would take
    // the whole shell down. Treat "nothing to ask" as an empty org list, which
    // renders as "No organization" rather than an error.
    if (!isConfigured()) {
      setState({ kind: "loaded", organizations: [] });
      return;
    }

    let cancelled = false;
    setState({ kind: "loading" });

    fetchOrganizations(tokenRef.current, { apiBaseUrl: API_BASE_URL() })
      .then((organizations) => {
        if (!cancelled) setState({ kind: "loaded", organizations });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          kind: "error",
          message:
            error instanceof OrganizationsError ? error.message : `could not load: ${String(error)}`,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return state;
}

export default function OrgSwitcher({
  state,
  selectedId,
  onSelect,
}: {
  state: OrgState;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (state.kind === "loading") return <Muted>Loading organizations…</Muted>;
  if (state.kind === "signed-out") return <Muted>Not signed in</Muted>;
  if (state.kind === "error") return <Muted>Organizations: {state.message}</Muted>;
  if (state.organizations.length === 0) return <Muted>No organizations</Muted>;

  return (
    <select
      data-org-switcher
      value={selectedId ?? state.organizations[0]?.id ?? ""}
      onChange={(event) => onSelect(event.target.value)}
      style={{ width: "100%", padding: "0.4rem", fontSize: "0.95rem" }}
    >
      {state.organizations.map((organization) => (
        <option key={organization.id} value={organization.id}>
          {organization.name}
        </option>
      ))}
    </select>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <p data-org-switcher style={{ color: "#888", margin: 0, fontSize: "0.9rem" }}>{children}</p>
  );
}
